import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, ShieldCheck, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { api, formatApiError } from '../../lib/api';
import { EditorShell, inputCls, labelCls } from './EditorShell';

const OAuthProviderEditor = ({ onClose }) => {
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState('google');
  const [clientId, setClientId] = useState('');
  const [clientSecret, setClientSecret] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const loadProviders = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/auth/oauth/providers');
      setProviders(data);
      if (!data.some((item) => item.id === 'google')) setProvider(data[0]?.id || '');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadProviders(); }, [loadProviders]);

  const selected = providers.find((item) => item.id === provider);
  const save = async () => {
    if (!clientId.trim() || !clientSecret) {
      toast.error('Fyll inn både client ID og secret key.');
      return;
    }
    setSaving(true);
    try {
      await api.put(`/admin/auth/oauth/providers/${provider}`, { client_id: clientId.trim(), client_secret: clientSecret });
      setClientId('');
      setClientSecret('');
      await loadProviders();
      toast.success('OAuth-nøklane er lagra kryptert.');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    setSaving(true);
    try {
      await api.delete(`/admin/auth/oauth/providers/${provider}`);
      setClientId('');
      setClientSecret('');
      await loadProviders();
      toast.success('Admin-lagra OAuth-nøklar er fjerna.');
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  return (
    <EditorShell title="OAuth-innloggingar" onClose={onClose} onSave={save} saving={saving} testId="oauth-provider-editor">
      {loading ? <div className="flex justify-center py-10"><Loader2 className="animate-spin text-white/50" /></div> : (
        <>
          <div>
            <label className={labelCls} htmlFor="oauth-provider-select">Leverandør</label>
            <select id="oauth-provider-select" className={inputCls} value={provider} onChange={(event) => { setProvider(event.target.value); setClientId(''); setClientSecret(''); }}>
              {providers.map((item) => <option key={item.id} value={item.id}>{item.label}</option>)}
            </select>
          </div>
          <p className="flex items-center gap-2 text-sm text-white/60">
            <ShieldCheck size={16} className="shrink-0 text-[#34E0A1]" />
            {selected?.configured ? `Credentials er sette via ${selected.source === 'admin' ? 'admin' : 'servermiljø'}.` : 'Ingen credentials er sett for denne leverandøren.'}
            {' '}Secret key blir kryptert og blir aldri vist tilbake.
          </p>
          <div>
            <label className={labelCls} htmlFor="oauth-client-id">{provider === 'tiktok' ? 'Client key' : 'Client ID'}</label>
            <input id="oauth-client-id" className={inputCls} value={clientId} onChange={(event) => setClientId(event.target.value)} autoComplete="off" />
          </div>
          <div>
            <label className={labelCls} htmlFor="oauth-client-secret">Secret key</label>
            <input id="oauth-client-secret" className={inputCls} type="password" value={clientSecret} onChange={(event) => setClientSecret(event.target.value)} autoComplete="new-password" />
          </div>
          {selected?.source === 'admin' && <button type="button" onClick={remove} disabled={saving} className="inline-flex items-center gap-2 text-sm text-red-300 hover:text-red-200 disabled:opacity-50"><Trash2 size={15} /> Fjern admin-lagra credentials</button>}
          <p className="text-xs leading-relaxed text-white/40">OAuth-hemmeligheiter må opprettast hos leverandøren først. Denne sida lagrar credentials sikkert, men kan ikkje opprette OAuth-appen eller callback-adressa hos leverandøren.</p>
        </>
      )}
    </EditorShell>
  );
};

export default OAuthProviderEditor;